import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { Filter, Search, UserPlus } from "lucide-react";
import SalesAffiliateLayout from "../components/SalesAffiliateLayout";
import SalesAffiliateLeadRegistrationForm from "../components/SalesAffiliateLeadRegistrationForm";
import SalesAffiliateLeadRow from "../components/SalesAffiliateLeadRow";
import { useSalesAffiliateWorkspace } from "../context/SalesAffiliateWorkspaceContext";
import { supabase } from "../lib/supabaseClient";
import {
  CONVERTED_STATUSES,
  DUPLICATE_STATUSES,
  INVALID_STATUSES,
  LEAD_SELECT_COLUMNS,
  WITHDRAWN_STATUSES,
  getSalesAffiliateFunctionErrorMessage,
  normalizeAffiliateLeadStatus,
  salesAffiliateFunctionCodeMessage,
} from "../lib/salesAffiliateCommercial";

export default function SalesAffiliateProspects() {
  const { loading, statusLoadFailed, workspaceData: status, configuration, lifecycleState } = useSalesAffiliateWorkspace();
  const [leads,setLeads]=useState([]);
  const [busy,setBusy]=useState(true);
  const [error,setError]=useState("");
  const [query,setQuery]=useState("");
  const [view,setView]=useState("ACTIVE");
  const load=useCallback(async()=>{
    setBusy(true); setError("");
    const {data,error}=await supabase.from("sales_affiliate_leads").select(LEAD_SELECT_COLUMNS).order("created_at",{ascending:false});
    if(error) setError("Unable to load prospect records right now. Please try again.");
    else setLeads(data||[]);
    setBusy(false);
  },[]);
  useEffect(()=>{ if(!loading&&!statusLoadFailed&&configuration.allowsDashboardPage) load(); },[loading,statusLoadFailed,configuration.allowsDashboardPage,load]);
  const filtered=useMemo(()=>leads.filter((lead)=>{
    const s=normalizeAffiliateLeadStatus(lead.status);
    if(view==="ACTIVE"&&s!=="ACCEPTED") return false;
    if(view==="CONVERTED"&&!CONVERTED_STATUSES.has(s)) return false;
    if(view==="WITHDRAWN"&&!WITHDRAWN_STATUSES.has(s)) return false;
    if(view==="DUPLICATE"&&!DUPLICATE_STATUSES.has(s)) return false;
    if(view==="INVALID"&&!INVALID_STATUSES.has(s)) return false;
    const q=query.trim().toLowerCase();
    return !q || [lead.company_name,lead.first_name,lead.last_name,lead.email].some(v=>String(v||"").toLowerCase().includes(q));
  }),[leads,query,view]);
  const handleWithdraw=async(id)=>{
    const {data,error}=await supabase.functions.invoke("sales_affiliate_lead_registration",{body:{action:"withdraw",lead_id:id}});
    if(error) {
      throw new Error(
        await getSalesAffiliateFunctionErrorMessage(
          error,
          "Unable to withdraw this protected prospect right now. Please try again.",
        ),
      );
    }
    if(!data?.ok) {
      throw new Error(
        salesAffiliateFunctionCodeMessage(
          data?.error,
          "Unable to withdraw this protected prospect right now. Please try again.",
        ),
      );
    }
    await load();
  };
  if(loading) return <SalesAffiliateLayout activeSection="prospects"><div className="p-10">Loading prospects…</div></SalesAffiliateLayout>;
  if(!configuration.allowsDashboardPage||!status?.affiliate_exists) return <Navigate to={configuration.defaultPath} replace/>;
  return <SalesAffiliateLayout activeSection="prospects" lifecycleState={lifecycleState}>
    <main className="mx-auto max-w-7xl px-6 py-8 sm:px-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div><div className="text-[11px] font-black uppercase tracking-[0.24em] text-blue-600">Protected Portfolio</div><h1 className="mt-2 text-4xl font-black text-slate-950">Prospects</h1><p className="mt-2 text-sm text-slate-600">Manage and follow up with your protected prospects in one focused workspace.</p></div>
      </div>
      <section className="mt-7"><SalesAffiliateLeadRegistrationForm onRegistered={load}/></section>
      <section className="mt-7 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="grid gap-3 md:grid-cols-[1fr_auto]">
          <div className="relative"><Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18}/><input aria-label="Search prospect records" value={query} onChange={e=>setQuery(e.target.value)} placeholder="Search prospects…" className="w-full rounded-2xl border border-slate-300 bg-white py-3 pl-11 pr-4 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"/></div>
          <div className="relative"><Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-600" size={16}/><select aria-label="Filter prospect records" value={view} onChange={e=>setView(e.target.value)} className="rounded-2xl border border-slate-300 bg-white py-3 pl-9 pr-10 text-sm font-black"><option value="ACTIVE">Active Protected</option><option value="ALL">All Records</option><option value="CONVERTED">Converted Customers</option><option value="WITHDRAWN">Withdrawn</option><option value="DUPLICATE">Duplicate Attempts</option><option value="INVALID">Not Accepted</option></select></div>
        </div>
        <div className="mt-6">
          {busy?<div className="rounded-2xl bg-slate-50 p-8 text-center text-sm font-semibold">Loading prospects…</div>:error?<div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-700">{error}</div>:filtered.length===0?<div className="rounded-2xl border border-slate-200 bg-slate-50 p-10 text-center"><UserPlus className="mx-auto text-blue-600"/><div className="mt-3 font-black">No protected prospects match this view</div></div>:<div className="space-y-4">{filtered.map(lead=><SalesAffiliateLeadRow key={lead.id} lead={lead} onWithdraw={handleWithdraw}/>)}</div>}
        </div>
      </section>
    </main>
  </SalesAffiliateLayout>;
}
